import React from "react"
import { Box, Button, CssBaseline, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Typography } from "@mui/material"
import { Route, Routes, useNavigate } from "react-router-dom"
import Searchbar from "./components/Searchbar"
import Sidebar from "./components/Sidebar"
import Footer from "./components/Footer"
import { LOGIN_INTERVAL } from "./global.js"
import { customFetch } from "./utils/functions"
import { enqueueSnackbar } from "notistack"
import BrandingService from "./branding/BrandingService.js"

// Lazy-loading allows the browser to load only the necessary components in chunks
const Login = React.lazy(() => import("./components/Login/Login"))
const PasswordReset = React.lazy(() => import("./components/PasswordReset"))
const Dashboard = React.lazy(() => import("./components/Dashboard/Dashboard"))
const VirtualTerminal = React.lazy(() => import("./components/Virtual Terminal/VirtualTerminal"))
const Invoices = React.lazy(() => import("./components/Invoices/Invoices"))
const InvoicePay = React.lazy(() => import("./components/Invoices/InvoicePay"))
const InvoiceReceipt = React.lazy(() => import("./components/Invoices/InvoiceReceipt"))
const Plans = React.lazy(() => import("./components/Plans/Plans"))
const Customers = React.lazy(() => import("./components/Customers/Customers"))
const BatchManager = React.lazy(() => import("./components/Batch Manager/BatchManager"))
const TransactionDetail = React.lazy(() => import("./components/Batch Manager/TransactionDetail"))
const Departments = React.lazy(() => import("./components/Inventory/Departments/Departments"))
const Categories = React.lazy(() => import("./components/Inventory/Categories/Categories"))
const Items = React.lazy(() => import("./components/Inventory/Items/Items"))
const Modifiers = React.lazy(() => import("./components/Inventory/Modifiers/Modifiers"))
const Vendors = React.lazy(() => import("./components/Inventory/Vendors/Vendors"))
const GiftCards = React.lazy(() => import("./components/Gift Cards/GiftCards"))
const Discounts = React.lazy(() => import("./components/Discounts/Discounts"))
const BatchSummary = React.lazy(() => import("./components/Reports/BatchSummary"))
const TransactionSearch = React.lazy(() => import("./components/Reports/TransactionSearch"))
const RecurringBilling = React.lazy(() => import("./components/Reports/RecurringBilling"))
const CardExpiration = React.lazy(() => import("./components/Reports/CardExpiration"))
const CurrentInventory = React.lazy(() => import("./components/Reports/CurrentInventory"))
const ItemsSold = React.lazy(() => import("./components/Reports/ItemsSold"))
const SalesTax = React.lazy(() => import("./components/Reports/SalesTax"))
const TotalSales = React.lazy(() => import("./components/Reports/Total Sales/TotalSales"))
const CashDrawer = React.lazy(() => import("./components/Reports/CashDrawer"))
const UserSales = React.lazy(() => import("./components/Reports/UserSales"))
const Tips = React.lazy(() => import("./components/Reports/Tips"))
const Ticket = React.lazy(() => import("./components/Tickets/Ticket"))
const Tickets = React.lazy(() => import("./components/Tickets/Tickets"))
const GeneralSettings = React.lazy(() => import("./components/Settings/General Settings/GeneralSettings"))
const MerchantSettings = React.lazy(() => import("./components/Settings/Merchant (CRM)/MerchantSettings"))
const Users = React.lazy(() => import("./components/Settings/Users/Users"))
const RenderPDF = React.lazy(() => import("./components/RenderPDF"))
const Approved = React.lazy(() => import("./components/Status/Approved"))
const Declined = React.lazy(() => import("./components/Status/Declined"))
const Page404 = React.lazy(() => import("./components/Page404"))
const CheckManager = React.lazy(() => import("./components/Check Manager/CheckManager"))
const CheckDetail = React.lazy(() => import("./components/Check Manager/CheckDetail"))
const ModGroups = React.lazy(() => import('./components/Inventory/Modifier Groups/ModGroups'))
const Employees = React.lazy(() => import('./components/Employees/Employees'))

export default function App() {
  // States
  const [merchants, setMerchants] = React.useState(JSON.parse(sessionStorage.getItem("merchants")) || [])
  const [merchant, setMerchant] = React.useState({})
  const [merchantSettings, setMerchantSettings] = React.useState({ pay: true })
  const [reefSettings, setReefSettings] = React.useState({})
  const [payLinkApiKey, setPayLinkApiKey] = React.useState({})

  // Hooks
  const loggedIn = Boolean(sessionStorage.getItem("token") && sessionStorage.getItem("username") && sessionStorage.getItem("role"))

  // Functions
  function fetchMerchants() {
    // Do not fetch merchants if the user is not logged in
    if (!loggedIn) return
    const role = sessionStorage.getItem("role")

    if (role === "Merchant") return // No additional merchant retrieval needed

    // Retrieve all Reef merchants
    if (role === "Admin" || role === "Office") {
      customFetch("GET", `${process.env.REACT_APP_API}/api/mongo/get-merchants?type=${BrandingService.current.id}`)
        .then(result => {
          const { data } = result
          let merchants = data.filter(m => m.client_type == BrandingService.current.id)
          setMerchants(data.sort((a, b) => { return a.dba < b.dba ? -1 : a.dba > b.dba ? 1 : 0 }))
        })
    }

    // Retrieve any merchants associated with an agent, and append with any merchants returned by default
    if (role === "Agent") {
      customFetch("POST", `${process.env.REACT_APP_API}/api/mongo/get-agent-merchants`, { aid: sessionStorage.getItem("agentID") })
        .then(result => {
          const { data } = result
          setMerchants(data.concat(merchants).sort((a, b) => { return a.dba < b.dba ? -1 : a.dba > b.dba ? 1 : 0 }))
        })
    }
  }

  function fetchMerchantSettings() {
    // Do not fetch merchant settings if the user is not logged in
    if (!loggedIn) return

    // Clear previous merchant settings
    setMerchantSettings({ pay: true })  // This avoids intrusive license dialog when loading

    if (!merchant.mid) return
    // Retrieve merchant settings from CRM
    customFetch("GET", `${process.env.REACT_APP_API}/api/mongo/get-document/Merchant/${merchant.mid}`)
      .then(result => setMerchantSettings(result.data))
      .catch(err => {
        console.log(err)
        setMerchantSettings({ pay: false }) // Show intrusive license dialog
      })
  }

  function fetchReefSettings() {
    if (!loggedIn) return
    if (!merchant.mid) return
    customFetch("POST", `${process.env.REACT_APP_API}/api/mongo/get-documents/Setting`, { mid: merchant.mid, ignoreDeletion: false, ignoreActive: true })
      .then(result => { setReefSettings(result.data[0] || {}) })
      .catch(err => console.log(err))
  }

  function updateMerchantSettings(merchant) {
    customFetch("POST", `${process.env.REACT_APP_API}/api/mongo/update-document/Merchant/${merchant._id}`, { $set: merchant })
      .then(() => { setMerchantSettings(merchant) })
  }

  function updateReefSettings(id, update) {
    customFetch("POST", `${process.env.REACT_APP_API}/api/mongo/update-document/Setting/${id}`, update)
      .then(result => {
        if (result) {
          enqueueSnackbar("Changes saved!", { variant: "success" })
          fetchReefSettings()
        } else enqueueSnackbar("Something went wrong.", { variant: "error" })
      })
  }

  function fetchApiKey() {
    const username = encodeURIComponent(merchantSettings.paylinkUsername)
    const password = encodeURIComponent(merchantSettings.paylinkPassword)
    if (username === "undefined" && password === "undefined") {
      setPayLinkApiKey(undefined)
      return
    }

    customFetch("GET", `${process.env.REACT_APP_API}/api/paylink/get-api-key?Username=${username}&Password=${password}`)
      .then(result => setPayLinkApiKey(result.data.Token))
      .catch(error => console.log(error))
  }

  function logout() {
    sessionStorage.removeItem("token")
    sessionStorage.removeItem("merchants")
    sessionStorage.removeItem("username")
    sessionStorage.removeItem("role")
    sessionStorage.removeItem("agentID")
    window.location.href = "/login"
  }

  function verifyLogin() {
    if (sessionStorage.getItem("token"))
      customFetch("GET", `${process.env.REACT_APP_API}/api/mongo/get-document/AccessLog/${sessionStorage.getItem("token")}`)
        .then(result => { if (!result.data.success) logout() })
  }

  // Effects
  React.useEffect(() => { fetchMerchants(); setInterval(verifyLogin, LOGIN_INTERVAL) }, [])
  React.useEffect(() => { setMerchant(merchants.find(m => m.mid === localStorage.getItem("lastMid")) || merchants[0] || {}) }, [merchants])
  React.useEffect(() => { fetchMerchantSettings(); fetchReefSettings() }, [merchant])
  React.useEffect(() => { fetchApiKey() }, [merchantSettings])

  return (
    <Box sx={{ maxWidth: "100vw" }}>
      {/* Intrusive dialog to prevent usage if licensing is disabled or missing */}
      {loggedIn && Boolean(merchantSettings.pay) === false && sessionStorage.getItem("role") === "Merchant" &&
        <Dialog open={true}>
          <DialogTitle align="center">
            <Typography variant="h4" color="error" fontWeight="bold">
              NO LICENSE
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography fontWeight="bold">
              Please contact your agent to confirm licensing for Reef Pay.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={logout}>
              Logout
            </Button>
          </DialogActions>
        </Dialog>}
      {Redirect(loggedIn)}
      <CssBaseline />
      {/* {loggedIn && <UserAgreement />} */}
      <Box display="flex" height="100%">
        {loggedIn && <Sidebar merchantSettings={merchantSettings} mid={merchant.mid} dba={merchant.dba} />}
        <Box display="flex" flexDirection="column" flex={1} justifyContent="space-between" height="100vh" width="86%">
          <Box>
            {loggedIn && <Searchbar merchants={merchants} merchant={merchant} setMerchant={setMerchant} />}
            <React.Suspense fallback={<LinearProgress />} key={location.href}>
              <Routes>
                <Route path="login" element={<Login />} />
                <Route
                  path="dashboard"
                  element={
                    <Dashboard
                      mid={merchant.mid}
                      merchantSettings={merchantSettings}
                    />
                  }
                />
                <Route
                  path="virtualterminal"
                  element={
                    <VirtualTerminal
                      mid={merchant.mid}
                      merchantSettings={merchantSettings}
                      paylinkApiKey={payLinkApiKey}
                      promptTip={reefSettings.promptTip}
                      address={reefSettings.addressToUse === "legal" ? merchantSettings.address : merchantSettings.dbaAddress}
                      defaultTaxRate={reefSettings.defaultTaxRate}
                    />
                  }
                />
                <Route path="invoices">
                  <Route
                    index
                    element={
                      <Invoices
                        mid={merchant.mid}
                        merchantSettings={merchantSettings}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        promptTip={reefSettings.promptTip != null ? reefSettings.promptTip : true}
                        promptShipping={reefSettings.promptShipping != null ? reefSettings.promptShipping : true}
                        promptDiscount={reefSettings.promptDiscount != null ? reefSettings.promptDiscount : true}
                        invoiceStartingNumber={reefSettings.invoiceStartingNumber || 0}
                        merchantEmail={reefSettings.email}
                        emailFrom={reefSettings.emailFrom}
                        subjectPrefix={reefSettings.subjectPrefix}
                        merchantLogo={reefSettings.logo}
                        defaultTaxRate={reefSettings.defaultTaxRate}
                      />
                    }
                  />
                  <Route path=":id" element={<InvoiceReceipt />} />
                </Route>
                <Route path="pay/:id" element={<InvoicePay />} />
                <Route
                  path="plans"
                  element={
                    <Plans
                      mid={merchant.mid}
                      pageSize={reefSettings.rowsReturnedPerPage || 10}
                      merchantSettings={merchantSettings}
                      defaultTaxRate={reefSettings.defaultTaxRate}
                    />
                  }
                />
                <Route
                  path="employee"
                  element={
                    <Employees
                      mid={merchant.mid}
                      pageSize={reefSettings.rowsReturnedPerPage || 10}
                    />
                  }
                />
                <Route
                  path="customers"
                  element={
                    <Customers
                      mid={merchant.mid}
                      pageSize={reefSettings.rowsReturnedPerPage || 10}
                      merchantSettings={merchantSettings}
                      paylinkApiKey={payLinkApiKey}
                    />
                  }
                />
                <Route path="batchmanager">
                  <Route
                    index
                    element={
                      <BatchManager
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                        paylinkApiKey={payLinkApiKey}
                      />
                    }
                  />
                  <Route
                    path="transaction"
                    element={
                      <TransactionDetail
                        mid={merchant.mid}
                        merchantSettings={merchantSettings}
                        address={reefSettings.addressToUse === "legal" ? merchantSettings.address : merchantSettings.dbaAddress}
                        paylinkApiKey={payLinkApiKey}
                      />
                    }
                  />
                </Route>
                <Route path="checkmanager">
                  <Route
                    index
                    element={
                      <CheckManager
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                      />
                    }
                  />
                  <Route
                    path="check"
                    element={
                      <CheckDetail
                        mid={merchant.mid}
                        merchantSettings={merchantSettings}
                        address={reefSettings.addressToUse === "legal" ? merchantSettings.address : merchantSettings.dbaAddress}
                      />
                    }
                  />
                </Route>
                <Route
                  path="giftcards"
                  element={
                    <GiftCards
                      mid={merchant.mid}
                      merchantSettings={merchantSettings}
                      paylinkApiKey={payLinkApiKey}
                      pageSize={reefSettings.rowsReturnedPerPage || 10}
                    />
                  }
                />
                <Route path="inventory">
                  <Route
                    path="departments"
                    element={
                      <Departments
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="categories"
                    element={
                      <Categories
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="items"
                    element={
                      <Items
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                      />
                    }
                  />
                  <Route
                    path="modifiers"
                    element={
                      <Modifiers
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="modgroups"
                    element={
                      <ModGroups
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="vendors"
                    element={
                      <Vendors
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                </Route>
                <Route
                  path="discounts"
                  element={
                    <Discounts
                      mid={merchant.mid}
                      pageSize={reefSettings.rowsReturnedPerPage || 10}
                    />
                  }
                />
                <Route path="reports">
                  <Route
                    path="batchsummary"
                    element={
                      <BatchSummary
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                      />
                    }
                  />
                  <Route
                    path="transactionsearch"
                    element={
                      <TransactionSearch
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                      />
                    }
                  />
                  <Route
                    path="recurringbilling"
                    element={
                      <RecurringBilling
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="cardexpiration"
                    element={
                      <CardExpiration
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="currentinventory"
                    element={
                      <CurrentInventory
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="itemssold"
                    element={
                      <ItemsSold
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="salestax"
                    element={
                      <SalesTax
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="totalsales"
                    element={
                      <TotalSales
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                      />
                    }
                  />
                  <Route
                    path="cashdrawer"
                    element={
                      <CashDrawer
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path="usersales"
                    element={
                      <UserSales
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                        merchantSettings={merchantSettings}
                      />
                    }
                  />
                  <Route
                    path="tips"
                    element={
                      <Tips
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                </Route>
                <Route path="pos">
                  <Route path="batchsummary" element={<BatchSummary pageSize={10} />} />
                  <Route path="transactionsearch" element={<TransactionSearch pageSize={10} />} />
                  <Route path="recurringbilling" element={<RecurringBilling pageSize={10} />} />
                  <Route path="cardexpiration" element={<CardExpiration pageSize={10} />} />
                  <Route path="currentinventory" element={<CurrentInventory pageSize={10} />} />
                  <Route path="itemssold" element={<ItemsSold pageSize={10} />} />
                  <Route path="salestax" element={<SalesTax pageSize={10} />} />
                  <Route path="totalsales" element={<TotalSales pageSize={10} />} />
                  <Route path="tips" element={<Tips pageSize={10} />} />
                </Route>
                <Route path="tickets">
                  <Route
                    index
                    element={
                      <Tickets
                        mid={merchant.mid}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      <Ticket
                        merchantName={merchant.dba}
                        merchantEmail={reefSettings.email}
                        paylinkApiKey={payLinkApiKey}
                      />
                    }
                  />
                </Route>
                <Route path="settings">
                  <Route
                    path="general"
                    element={
                      <GeneralSettings
                        mid={merchant.mid}
                        settings={reefSettings}
                        updateSettings={updateReefSettings}
                      />
                    }
                  />
                  <Route
                    path="merchant"
                    element={
                      <MerchantSettings
                        mid={merchant.mid}
                        merchantSettings={merchantSettings}
                        settings={reefSettings}
                        updateSettings={updateReefSettings}
                        updateMerchant={updateMerchantSettings}
                      />
                    }
                  />
                  <Route
                    path="usermanager"
                    element={
                      <Users
                        merchants={merchants}
                        pageSize={reefSettings.rowsReturnedPerPage || 10}
                      />
                    }
                  />
                </Route>
                <Route
                  path="password/:token"
                  element={<PasswordReset />}
                />
                <Route path="pdf/:file" element={<RenderPDF />} />
                <Route path="*" element={<Page404 />} />
                <Route path="status">
                  <Route
                    path="complete"
                    element={<Approved />}
                  />
                  <Route
                    path="failure"
                    element={<Declined />}
                  />
                </Route>
              </Routes>
            </React.Suspense>
          </Box>
          {loggedIn && <Footer />}
        </Box>
      </Box >
    </Box>
  )
}

function Redirect(loggedIn) {
  const navigate = useNavigate()
  // If login page is accessed while already being logged in
  // redirect to the default page
  if (loggedIn && window.location.pathname === "/login")
    navigate("/dashboard")

  // Redirect if page is not ignored
  if (!loggedIn) {
    const REDIRECT_IGNORE = ["login", "pay", "pdf", "pos", "status", "password"]
    let path = window.location.pathname.split("/")
    for (const ignore of REDIRECT_IGNORE)
      if (path.includes(ignore)) return
    navigate("/login")
  }
}